/**
 * 来源条 / 详情页上「这次实际按什么目的、多长编排」的措辞单点(2026-08-02)。
 *
 * ═══ 为什么这件事值得一个模块 + 一组测试 ════════════════════════════════════════
 * `effectivePurpose.ts` 留着这段告诫(波3-⑦):
 *
 * > 措辞必须是「按 X 编排」而不是「已识别为 X」:后者会让人以为 AI 读懂了他写的东西,
 * > 那就是概览比实际乐观(本仓踩过三次)。
 *
 * 那时它完全正确 —— 引擎 `select_playbook` 只对 purpose **字符串**做关键词匹配,
 * 根本不看 userInput,没给就一律走「产品带货」。
 *
 * **2026-08-02 起这个前提变了**:ai 侧新增 `intent_infer`,真的从文案推目的与时长,
 * 并把 `resolvedPurpose` / `purposeSource` / `resolvedDurationSec` / `durationSource`
 * 随 compose 响应回传(→ backend `mergeStoryboard` 直接落 formJson,api 零改动)。
 *
 * 于是那句话从「保守」变成了「说少了」:AI 明明判出了「公司介绍」,界面还写着
 * 「未选目的 → 按产品带货编排」。**撒谎的方向反了,但仍然是撒谎。**
 *
 * ★三态必须分得开(与 ai 侧 `ResolvedIntent.*_source` 逐字同源):
 *   - `user`     用户自己选的 —— 不提推断也不提兜底;
 *   - `inferred` 从文案判的 —— 说是「按你的文案判的」并**给出依据**(不可审计的推断=黑箱);
 *   - `fallback` 没判出来 —— 老实说「兜底」,原告诫在这一支上继续有效。
 * 还有第四态:**编排之前**。此刻我们不知道会判成什么,只能说「会按你的文案判」——
 * 在这里写死一个档就是旧版那句谎的镜像(codex 抓过两次的「概览比实际乐观」同型)。
 *
 * ★console 只**显示**,不自己推断。ai 侧那份 `effectivePurpose.ts` 的告诫里写得很清楚:
 *   「console 本地关键词推断 → 与引擎两份判据,必漂移(本仓刚为『两份判据』吃过两次亏)」。
 */

/** compose 回传的出处字段(经 backend mergeStoryboard 落 formJson;老草稿全缺)。 */
export interface IntentProvenance {
  resolvedPurpose?: string | null;
  purposeSource?: string | null;
  purposeReason?: string | null;
  resolvedDurationSec?: number | null;
  durationSource?: string | null;
  durationReason?: string | null;
  /** AI 反对意见(2026-08-10):用户手选的档与文案信号分歧时,引擎推断的那一档;无分歧为空。 */
  purposeHint?: string | null;
  purposeHintReason?: string | null;
}

export interface PurposeNotice {
  /** 四态:user / inferred / fallback / pending(还没编排,或老草稿没这些字段)。 */
  tone: "user" | "inferred" | "fallback" | "pending";
  /** 一行主文案。 */
  text: string;
  /** 推断依据(只有 inferred 且引擎给了 reason 时才有)。 */
  detail?: string;
  /** 时长那一行;没有时长信息时不渲染。 */
  durationText?: string;
}

/** 从 reason 里把引擎命中的那个词抠出来(`…:「专业机构」` → `专业机构`)。 */
function keyword(reason?: string | null): string | undefined {
  const m = /[:：]「(.+?)」/.exec(reason ?? "");
  return m?.[1];
}

export function purposeNotice(p: IntentProvenance): PurposeNotice {
  const label = (p.resolvedPurpose ?? "").trim();
  const src = (p.purposeSource ?? "").trim();

  let durationText: string | undefined;
  if (p.resolvedDurationSec) {
    durationText =
      p.durationSource === "user"
        ? `时长 ${p.resolvedDurationSec}s(你选的档)`
        : `时长 ${p.resolvedDurationSec}s(AI 按内容定的${
            p.durationReason ? `:${p.durationReason}` : ""
          })`;
  }

  // ★老草稿(本次改动之前编排的)只有 resolvedPurpose 缺 purposeSource,或两者都没有 ——
  //   一律按 pending 处理。拿一个没有出处的档去说「AI 判的」就是我们自己造假。
  if (!label || !src) {
    return {
      tone: "pending",
      // ★措辞不带方位词(2026-08-06 重排):原文是「想指定方向点右边」,指的是来源条右侧那颗
      //   目的 popover。目的提成备料台常驻行之后那句就成了谎 —— 而它是**布局一变就失效**的
      //   那类文案。改成只说事实、不说位置:同一份措辞在来源条 / 目的行 / 摘要条都成立。
      text: "没选目的也能出片:AI 会按你的文案判该怎么拍",
      durationText,
    };
  }

  if (src === "user") {
    // AI 反对意见:尊重用户的选择,但机器有不同判断时不再沉默(400 电话真单:选「品牌宣传」
    // 贴业务百科,机器本会判「公司介绍」,全程没吭声 —— 用户拿到拧的片子无从归因)。
    const hint = (p.purposeHint ?? "").trim();
    const hintKw = keyword(p.purposeHintReason);
    return {
      tone: "user",
      text: `按「${label}」编排`,
      detail:
        hint && hint !== label
          ? `按你的文案,AI 会判「${hint}」${hintKw ? `(文案里的「${hintKw}」)` : ""} —— 保留你的选择;效果不合适可以换档重编排`
          : undefined,
      durationText,
    };
  }
  if (src === "inferred") {
    const kw = keyword(p.purposeReason);
    return {
      tone: "inferred",
      text: `AI 按你的文案判为「${label}」编排;不对可以改`,
      detail: kw ? `依据:文案里的「${kw}」` : undefined,
      durationText,
    };
  }
  // fallback:原告诫继续有效 —— 说「兜底」,不说「识别/读懂」。
  return {
    tone: "fallback",
    text: `文案里没看出明确方向 → 按兜底档「${label}」编排`,
    durationText,
  };
}

/**
 * 出片请求的意图出处随行包 → `sourceRef` 自由 Map 通道(与蓝图溯源/拍法档案同范式,api 零改动)。
 *
 * ★为什么要随任务落(2026-08-02 T1 验收账发现):意图出处此前只活在草稿 formJson,
 * 任务 request_payload 里 `purpose` 恒空(用户没手选就不发)——
 * ① 渲染期 `derive_grade_look` 拿不到目的,briefStyle 为空时按「产品带货」兜底调色;
 * ② 详情页「本次设置」与 video_audit 的目的分布全是(空);
 * ③ retry 重放 request_payload,丢了就是永远丢。
 * 「是谁定的」(purposeSource)必须随行 —— 只送 effective purpose 会把 AI 推断显示成用户选择
 * (波2 修掉的那个错的任务版)。
 */
export function intentProvenanceRef(p: IntentProvenance): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (p.resolvedPurpose) out.resolvedPurpose = p.resolvedPurpose;
  if (p.purposeSource) out.purposeSource = p.purposeSource;
  if (p.purposeReason) out.purposeReason = p.purposeReason;
  if (p.resolvedDurationSec) out.resolvedDurationSec = p.resolvedDurationSec;
  if (p.durationSource) out.durationSource = p.durationSource;
  return out;
}

/**
 * 本次实际按什么目的出片(effective):用户选的永远赢;没选用 AI 判的;都没有返 undefined
 * (backend/渲染端各自兜底)。进任务顶层 `purpose` 字段 —— 渲染期 grade_look、详情、audit 消费。
 */
export function effectiveTaskPurpose(p: {
  briefPurpose?: string | null;
  resolvedPurpose?: string | null;
}): string | undefined {
  return p.briefPurpose ?? p.resolvedPurpose ?? undefined;
}
